/**
 * The farmer's session, kept entirely separate from staff.
 *
 * Different storage keys on purpose: a field agent and a farmer may well share a
 * phone, and signing one in must never sign the other out or — far worse — hand one
 * the other's token. The two never touch.
 *
 * Farmer tokens are long-lived (60 days) and there is no refresh dance: a smallholder
 * on patchy signal should not be re-authenticating, and the portal is read-mostly.
 */
import { ApiError } from './api'

const TOKEN_KEY = 'ledger.farmer.token'
const WHO_KEY = 'ledger.farmer.who'

export interface FarmerIdentity {
  id: string
  name: string
  msisdn: string
  kycStatus: string
}

export const farmerSession = {
  get token() {
    return localStorage.getItem(TOKEN_KEY)
  },
  get who(): FarmerIdentity | null {
    const raw = localStorage.getItem(WHO_KEY)
    return raw ? (JSON.parse(raw) as FarmerIdentity) : null
  },
  get isSignedIn() {
    return !!localStorage.getItem(TOKEN_KEY)
  },
  set(token: string, who: FarmerIdentity) {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(WHO_KEY, JSON.stringify(who))
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(WHO_KEY)
  },
}

async function parse(res: Response) {
  const text = await res.text()
  if (!text.trim()) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

async function request<T>(method: string, path: string, body?: unknown, auth = true): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = farmerSession.token
  if (auth && token) headers.Authorization = `Bearer ${token}`

  let res: Response
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiError(0, 'OFFLINE', 'No connection. Try again when you have network.')
  }

  const payload = await parse(res)

  if (!res.ok) {
    // An expired token is the one case worth handling here: sign out quietly and let
    // the shell show the phone-number screen rather than an error the farmer cannot act on.
    if (res.status === 401 && auth && token) farmerSession.clear()

    const err = (payload as { error?: { code?: string; message?: string } })?.error
    throw new ApiError(
      res.status,
      err?.code ?? `HTTP_${res.status}`,
      err?.message ?? 'Something went wrong. Try again.',
    )
  }

  return payload as T
}

export const farmerApi = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),

  requestCode: (msisdn: string) =>
    request<{ sent: boolean; message: string; devCode?: string; devNote?: string }>(
      'POST', '/farmer-auth/request-code', { msisdn }, false),

  async verify(msisdn: string, code: string) {
    const res = await request<{ accessToken: string; farmer: FarmerIdentity }>(
      'POST', '/farmer-auth/verify', { msisdn, code }, false)
    farmerSession.set(res.accessToken, res.farmer)
    return res.farmer
  },

  signOut() {
    farmerSession.clear()
  },
}

// ---------------------------------------------------------------- shapes
export interface Buyable {
  cropId: string
  cropName: string
  localName: string | null
  gradeId: string
  gradeCode: string
  gradeName: string
  specText: string
  rank: number
  pricePerKg: number
}

export interface Offer {
  id: string
  code: string
  status: string
  source: string
  estimatedKg: number
  readyFrom: string
  readyTo: string | null
  notes: string | null
  indicativePricePerKg: number | null
  indicativeValue: number | null
  scheduledFor: string | null
  declinedReason: string | null
  withdrawnReason: string | null
  collectedKg: number | null
  collectedAt: string | null
  createdAt: string
  cropName: string
  cropId: string
  claimedGradeCode: string | null
  farmerName: string
  farmerCode: string
  farmerId: string
  zoneName: string | null
  collectionCentreName: string | null
  lotCode: string | null
  fulfilmentPct: number | null
}

export interface Delivery {
  lotCode: string
  cropName: string
  gradeCode: string
  gradeSpec: string
  weighedKg: number
  pricePerKg: number
  amount: number
  collectedAt: string
  weighedAt: string | null
  weighedBy: string | null
  scaleCode: string | null
  scaleCertificate: string | null
  paymentStatus: string | null
  paidAmount: number | null
  paidAt: string | null
  receipt: string | null
  paidTo: string | null
}

export interface FarmerProfile {
  id: string
  code: string
  name: string
  msisdn: string
  kycStatus: string
  zone: string | null
  payoutAccount: { accountRef: string; accountName: string; channel: string; nameMatchState: string } | null
  farms: Array<{ id: string; name: string | null; acreage: number | null }>
  totals: { lots: number; kg: number; paid: number }
  reliability: { offers: number; collected: number; fulfilmentPct: number | null; avgEstimateErrorPct: number | null } | null
  openOffers: number
}

/** Plain-language status, because "part_collected" means nothing to a farmer. */
export function offerStatusLabel(status: string): { text: string; tone: 'ok' | 'warn' | 'crit' | 'info' | 'neutral' } {
  switch (status) {
    case 'open': return { text: 'Waiting for a reply', tone: 'info' }
    case 'accepted': return { text: 'Accepted — date coming', tone: 'ok' }
    case 'scheduled': return { text: 'We are coming', tone: 'ok' }
    case 'collected': return { text: 'Collected', tone: 'neutral' }
    case 'part_collected': return { text: 'Collected (less than offered)', tone: 'warn' }
    case 'declined': return { text: 'Not needed this time', tone: 'warn' }
    case 'withdrawn': return { text: 'You cancelled this', tone: 'neutral' }
    case 'expired': return { text: 'Date passed', tone: 'neutral' }
    default: return { text: status, tone: 'neutral' }
  }
}
